/* eslint-disable no-console */
import React, { useState } from "react";
import Button2 from "../../../shared/components/button/Button2";
import {
  Send,
  X,
  User,
  Mail,
  MessageSquare,
  UserCircle,
  Check,
  Cross,
  CircleX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { sendProductInfoRequest } from "./common";

const RequestQuoteModal = ({
  onClose,
  product,
}: {
  onClose: () => void;
  product: any;
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    lastname: string;
    email: string;
    question: string;
  }>({
    name: "",
    lastname: "",
    email: "",
    question: "",
  });

  const [errors, setErrors] = useState<{
    name?: string;
    lastname?: string;
    email?: string;
    question?: string;
  }>({});

  const [isSuccess, setIsSuccess] = useState(false);

  const handleFormChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.lastname.trim()) newErrors.lastname = "Lastname is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email format";
    if (!formData.question.trim()) newErrors.question = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendRequest = async () => {
    if (!validateForm()) return;
    try {
      await sendProductInfoRequest(formData, product.name);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Error sending request:", error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-800/80 backdrop-blur-sm flex justify-center items-center p-4 z-50"
      >
        {!isSuccess ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white dark:bg-gray-900 w-full max-w-md p-6 rounded-2xl shadow-2xl"
          >
            <div className="flex items-center justify-between mb-6">
              <motion.h2
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="text-2xl font-bold text-gray-800"
              >
                Richiedi Informazioni
              </motion.h2>
              <motion.button
                whileHover={{ rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X size={24} />
              </motion.button>
            </div>

            <form className="space-y-5">
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="relative"
              >
                <label className="text-md font-semibold mb-2 text-gray-700 dark:text-gray-300 block">
                  Name
                </label>
                <div className="relative">
                  <User
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 
                    dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your name"
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1 flex-row"><CircleX size={16} /> {errors.name}</p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <label className="text-md font-semibold mb-2 text-gray-700 dark:text-gray-300 block">
                  Lastname
                </label>
                <div className="relative">
                  <UserCircle
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="text"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleFormChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 
                    dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your lastname"
                  />
                </div>
                {errors.lastname && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1 flex-row"><CircleX size={16} /> {errors.lastname}</p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="relative"
              >
                <label className="text-md font-semibold mb-2 text-gray-700 dark:text-gray-300 block">
                  Email
                </label>
                <div className="relative">
                  <Mail
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={18}
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 
                    dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="your@email.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2 flex items-center gap-1 flex-row"><CircleX size={16} /> {errors.email}</p>
                )}
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="relative"
              >
                <label className="text-md font-semibold mb-2 text-gray-700 dark:text-gray-300 block">
                  Info
                </label>
                <div className="relative">
                  <MessageSquare
                    className="absolute left-3 top-3 text-gray-400"
                    size={18}
                  />
                  <textarea
                    name="question"
                    value={formData.question}
                    onChange={handleFormChange}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 
                    dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all
                    min-h-[100px] resize-none"
                    placeholder="Your message here..."
                  />
                </div>
                {errors.question && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1 flex-row"><CircleX size={16} /> {errors.question}</p>
                )}
              </motion.div>


              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex justify-end space-x-4 pt-4"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button2
                    icon={X}
                    title="CANCEL"
                    onClick={onClose}
                  //   className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                  />
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button2
                    icon={Send}
                    title="INVIA RICHIESTA"
                    onClick={handleSendRequest}
                    className="bg-green-500 hover:bg-green-700 text-white"
                  />
                </motion.div>
              </motion.div>
            </form>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-white/95 dark:bg-gray-900/95 rounded-2xl 
                    flex flex-col items-center justify-center z-10"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
              }}
              className="bg-green-100 dark:bg-green-900/30 rounded-full p-6 mb-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-green-500 rounded-full p-3"
              >
                <Check className="w-8 h-8 text-white" />
              </motion.div>
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2"
            >
              Request Sent Successfully!
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-gray-600 dark:text-gray-400 text-center max-w-xs"
            >
              Thank you for your request. We'll get back to you soon.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-6"
            >
              <motion.div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full w-32">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{
                    duration: 2,
                    ease: "easeInOut",
                  }}
                  className="h-full bg-green-500 rounded-full"
                />
              </motion.div>
            </motion.div>
            {/* Button to close the modal */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="mt-6"
            >
              <Button2
                icon={X}
                title="CLOSE"
                onClick={onClose}
                className="bg-green-500 hover:bg-green-700 text-white"
              />
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default RequestQuoteModal;
